import PropTypes from "prop-types";
import { Text } from "@react-spectrum/s2";

import { style, size } from "@react-spectrum/s2/style" with { type: "macro" };

/**
 * A caption text — a label or description — that can be overridden:
 * `undefined` keeps the `fallback` (the default), while `null` hides it.
 */
export const resolveCaptionText = (value, fallback) =>
  value === null ? null : (value ?? fallback);

/**
 * The description of an image thumbnail — a template asset or an image
 * variation — shown below it.
 */
export const ThumbnailDescription = ({ description }) =>
  !!description && (
    <Text styles={style({ font: "detail-sm" })}>{description}</Text>
  );

ThumbnailDescription.propTypes = {
  description: PropTypes.string,
};

/**
 * What captions an image thumbnail — a template asset or an image variation —
 * below it, in a single row: its `contextualHelp` (when it has any), its
 * `description`, and its `actions` (right-aligned). Each is left out when
 * empty. Its label is a `LabeledValue`, above the thumbnail.
 */
export const ThumbnailCaption = ({ description, contextualHelp, actions }) => {
  if (!description && !contextualHelp && !actions) {
    return null;
  }

  return (
    <div
      className={style({
        display: "flex",
        alignItems: "start",
        gap: 4,
        marginTop: 8,
        paddingX: size(6),
      })}
    >
      {contextualHelp}
      {!!description && (
        <div className={style({ flexGrow: 1, minWidth: 0 })}>
          <ThumbnailDescription description={description} />
        </div>
      )}
      {!!actions && (
        <div
          className={style({
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginStart: "auto",
            flexShrink: 0,
          })}
        >
          {actions}
        </div>
      )}
    </div>
  );
};

ThumbnailCaption.propTypes = {
  description: PropTypes.string,
  contextualHelp: PropTypes.node,
  actions: PropTypes.node,
};
